import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import {useParams} from 'react-router-dom'
import Button from '@material-ui/core/Button'
import cn from 'classnames'

import messages from '../../../../../../../assets/messages'
import {ManagerOffer} from '../../useOffers'
import {PerformanceFee} from './components/PerformanceFee'
import {LargeText} from './components/LargeText'
import {getFormattedAmount} from '../../../../../../../common/utils'
import {config} from '../../../../../../../config'
import {WithCopyToClipboard} from '../../../../../../Common/WithCopyToClipboard'
import {InfoTooltip} from './components/InfoTooltip'
import {useStyles} from './styles'

type OfferTileProps = {
  offer: ManagerOffer;
} & WithNamespaces;

export const mapIntervalNameToI18nKeyPlural: Record<ManagerOffer['settings']['tradingInterval']['type'], {i18nKey: string, defaults: string}> = {
  'Days': messages.days,
  'Weeks': messages.weeks,
  'Months': messages.months,
  'CalendarMonths': messages.calendarMonths,
}

export const mapIntervalNameToI18nKeySingular: Record<ManagerOffer['settings']['tradingInterval']['type'], {i18nKey: string, defaults: string}> = {
  'Days': messages.day,
  'Weeks': messages.week,
  'Months': messages.month,
  'CalendarMonths': messages.calendarMonth,
}

const _OfferTile = ({offer, t}: OfferTileProps) => {
  const classes = useStyles()
  const params = useParams<{accountId?: string}>()
  const {id: offerId, name, settings, activeInvestmentCount, currency} = offer
  const joinLink = `${config.frontendUrl}?pammManager=${params.accountId}&pammOffer=${offerId}`

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          <div className={classes.headerTitleName}>
            {name}
          </div>
          <div className={classes.headerTitleInvestors}>
            <Trans {...messages.investors} values={{activeInvestmentCount}} />
          </div>
        </div>
        <div>
          <WithCopyToClipboard text={joinLink}>
            <Button variant="contained" color="primary">
              <Trans {...messages.copyLink} />
            </Button>
          </WithCopyToClipboard>
        </div>
      </div>

      <div className={classes.divider} />

      <div className={classes.body}>
        <div className={classes.bodyColumn}>
          <div className={classes.infoTitle}>
            <Trans {...messages.feeStructureHeader} />
            <InfoTooltip content={t('feeStructureHeaderTooltip')} />
          </div>
          <div className={classes.feeTile}>
            <PerformanceFee fees={settings.performanceFees} currency={currency} />
          </div>
        </div>
        <div className={classes.bodyColumn}>
          <div className={classes.text}><Trans {...messages.tradingInterval} /></div>
          <div className={classes.text}>
            <LargeText>
              {settings.tradingInterval.count}
              {' '}
              <Trans
                {...(settings.tradingInterval.count === 1 ? mapIntervalNameToI18nKeySingular : mapIntervalNameToI18nKeyPlural)[settings.tradingInterval.type]}
              />
            </LargeText>
          </div>
        </div>
        <div className={classes.bodyColumn}>
          <div className={classes.text}><Trans {...messages.minDeposit} /></div>
          <div className={cn(classes.text, classes.greenAmount, classes.amount)}>
            {getFormattedAmount({amount: settings.minDeposit, currency})}
          </div>
        </div>
        <div className={classes.bodyColumn}>
          <div className={classes.text}>
            <Trans {...messages.minWithdrawal} />
          </div>
          <div className={cn(classes.text, classes.amount)}>
            {getFormattedAmount({amount: settings.minWithdrawal, currency})}
          </div>
        </div>
        <div className={classes.bodyColumn}>
          <div className={classes.text}>
            <Trans {...messages.minInitialInvestment} />
          </div>
          <div className={cn(classes.text, classes.greenAmount, classes.amount)}>
            {getFormattedAmount({amount: settings.minInitialInvestment, currency})}
          </div>
        </div>
      </div>
    </div>
  )
}

export const OfferTile = withNamespaces()(_OfferTile)
